import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { Root } from '@/hooks/useAlgoliaSearch/types';

import {
  ALGOLIA_APP_ID,
  ALGOLIA_API_KEY,
  PUBLIC_SWS_ENV,
} from '@/constants/env';
import algoliasearch from 'algoliasearch/lite';
import { sentryCaptureMessage } from '@/components/Sentry';

const algoliaAppId = ALGOLIA_APP_ID;
const algoliaApiKey = ALGOLIA_API_KEY;

export const filterHits = (hits: Root[], include: Root['type'][]): Root[] => {
  return hits.filter((result) => {
    return include.includes(result.type);
  });
};

export const useCompanyBatchSearch = () => {
  const index =
    PUBLIC_SWS_ENV === 'dev' || PUBLIC_SWS_ENV === 'local'
      ? 'companies-dev'
      : 'companies';

  const queryClient = useQueryClient();
  const algoliaClient = algoliasearch(algoliaAppId, algoliaApiKey);

  const searchCompanies = useCallback(
    (queries: (string | null)[]) => {
      const validQueries = queries.filter((q): q is string => q !== null);

      return queryClient.fetchQuery({
        queryKey: ['TRANSACTION_COMPANY_BATCH_SEARCH', queries],
        queryFn: async () => {
          try {
            const response = await algoliaClient.search(
              validQueries.map((query) => ({
                indexName: index,
                query,
                params: {
                  highlightPostTag: ' ',
                  highlightPreTag: ' ',
                  restrictHighlightAndSnippetArrays: true,
                },
              }))
            );

            if (typeof response !== 'undefined') {
              return response.results.map((result) => {
                const hitsWithQuery = result.hits.map((hit) => ({
                  ...hit,
                  query: result.query,
                }));

                return filterHits(hitsWithQuery as Root[], ['company']);
              });
            }
            return [];
          } catch (error) {
            sentryCaptureMessage({
              team: 'wombats',
              message: 'Algolia batch search failed',
              extra: {
                error: error instanceof Error ? error.message : 'Unknown error',
              },
            });
            return [];
          }
        },
        staleTime: 3600,
      });
    },
    [queryClient, algoliaClient]
  );

  return {
    searchCompanies,
  };
};

export type SearchCompaniesResult = Awaited<
  ReturnType<ReturnType<typeof useCompanyBatchSearch>['searchCompanies']>
>;
